import Card from "../../../../../components/shared/Card/Card";
import { clamp100 } from "../../../../../utils/clamp100";
import { formatUnit } from "../../../utils/formatUnit";
import { ReportIndicatorChart } from "./ReportIndicatorChart";

const BG_BY_TONE = {
  green: "success25",
  orange: "warning25",
  red: "error25",
};

const TEXT_BY_TONE = {
  green: "text-success700",
  orange: "text-warning700",
  red: "text-error700",
};

const ICON_BY_TONE = {
  green: "/images/pdf-report/green.svg",
  orange: "/images/pdf-report/orange.svg",
  orangeUp: "/images/pdf-report/orange-up.svg",
  red: "/images/pdf-report/red.svg",
  redUp: "/images/pdf-report/red-up.svg",
};

function iconFor(tone, z) {
  if (tone === "green") return ICON_BY_TONE.green;
  if (tone === "orange")
    return z && z >= 0 ? ICON_BY_TONE.orangeUp : ICON_BY_TONE.orange;
  if (tone === "red")
    return z && z >= 0 ? ICON_BY_TONE.redUp : ICON_BY_TONE.red;
  return ICON_BY_TONE.green;
}

export const ReportIndicatorCard = ({
  additionalDataText = "Z Score",
  chartReferenceArea,
  chartValues,
  color,
  indicatorName,
  indicatorValue,
  indicatorValueDecimalPlaces = null,
  percentage,
  showAdditionalData,
  showChart,
  showIcons,
  unit,
  zScore,
}) => {
  const pct = clamp100(Number(percentage || 0));
  const nameIsShort = indicatorName.length < 20;

  return (
    <Card
      borderColor="gray200"
      className="flex flex-none w-[calc((100%-1.25rem)/3)] flex-col items-center p-1.5"
      color={BG_BY_TONE[color]}
      rounded="md"
    >
      {/* Score */}
      <div className="mb-1 text-[10px] text-black">
        {pct.toFixed(0)}
        <span className="text-grayTrue500">/100</span>
      </div>

      {/* Title */}
      <div
        className={[
          "text-center leading-3 font-bold text-black",
          nameIsShort ? "text-[10px]" : "text-[9px]",
        ].join(" ")}
      >
        {indicatorName}
      </div>

      {/* Value + icon */}
      <div className="flex w-full items-center justify-center gap-1 py-2.5">
        {showIcons && (
          <img
            alt="indicator-direction"
            className="size-3"
            src={iconFor(color, zScore)}
          />
        )}

        <div
          className={[
            "text-sm leading-none font-bold",
            TEXT_BY_TONE[color],
          ].join(" ")}
        >
          {formatUnit(unit, indicatorValue, {
            convert: false,
            dp: indicatorValueDecimalPlaces ?? undefined,
          })}
        </div>
      </div>

      {/* Mini chart */}
      {showChart && chartReferenceArea && chartValues && (
        <ReportIndicatorChart
          referenceLineCoordinates={chartReferenceArea}
          values={chartValues}
        />
      )}

      {/* Additional data / z-score */}
      {showAdditionalData && (
        <div
          className="mt-auto flex w-full justify-between pt-1.5"
          style={{ borderTop: "1px dashed #A3A3A3" }}
        >
          <div className="text-[9px] text-black">{additionalDataText}</div>
          <div className="text-grayTrue600 text-[9px]">{zScore}</div>
        </div>
      )}

      {/* Fallback label when additional block is hidden */}
      {!showAdditionalData && !indicatorName.includes("Order") && (
        <div className="text-grayTrue600 text-[9px]">{additionalDataText}</div>
      )}
    </Card>
  );
};
